// Official UCAT ANZ section order and per-question pacing, derived from
// https://www.ucat.edu.au/about-ucat-anz/test-format/ (standard, non-SEN
// timing): VR 44Q/22min, DM 35Q/37min, QR 36Q/26min, SJT 69Q/26min. Applying
// each section's official seconds-per-question to however many questions of
// that section actually exist in a given paper gives a fair, proportional
// section time limit for both full (185Q) and mini (45Q) papers, rather
// than hardcoding one blanket exam-length timer.
export const SECTION_ORDER = ['VR', 'DM', 'QR', 'SJT'];

export const SECTION_NAMES = {
  VR: 'Verbal Reasoning',
  DM: 'Decision Making',
  QR: 'Quantitative Reasoning',
  SJT: 'Situational Judgement',
};

const OFFICIAL_SECONDS_PER_QUESTION = {
  VR: Math.round((22 * 60) / 44),
  DM: Math.round((37 * 60) / 35),
  QR: Math.round((26 * 60) / 36),
  SJT: Math.round((26 * 60) / 69),
};

// Groups a flat question list (each with a .section field) into ordered
// sections, each carrying its own time limit computed from official pacing.
export function groupIntoSections(questions) {
  const bySection = {};
  for (const code of SECTION_ORDER) bySection[code] = [];
  for (const q of questions) {
    if (bySection[q.section]) bySection[q.section].push(q);
  }
  return SECTION_ORDER.filter((code) => bySection[code].length > 0).map((code) => ({
    code,
    name: SECTION_NAMES[code],
    questions: bySection[code],
    timeLimitSeconds: bySection[code].length * OFFICIAL_SECONDS_PER_QUESTION[code],
  }));
}

export function totalTimeSeconds(sections) {
  return sections.reduce((sum, s) => sum + s.timeLimitSeconds, 0);
}

export function totalQuestions(sections) {
  return sections.reduce((sum, s) => sum + s.questions.length, 0);
}

// Maps a flat (section-agnostic) question index — the shape saved_answers/
// current_question_index already use in the DB — to a {sectionIndex,
// questionIndex} pair, so resume works without changing the stored shape.
export function locateFlatIndex(sections, flatIndex) {
  let remaining = flatIndex;
  for (let s = 0; s < sections.length; s++) {
    if (remaining < sections[s].questions.length) {
      return { sectionIndex: s, questionIndex: remaining };
    }
    remaining -= sections[s].questions.length;
  }
  return { sectionIndex: 0, questionIndex: 0 };
}

export function flattenIndex(sections, sectionIndex, questionIndex) {
  let flat = 0;
  for (let s = 0; s < sectionIndex; s++) flat += sections[s].questions.length;
  return flat + questionIndex;
}

export function formatDuration(seconds) {
  const total = Math.max(0, seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) {
    return `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }
  return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
}

export function formatMinutes(seconds) {
  const mins = Math.round(seconds / 60);
  return `${mins} minute${mins === 1 ? '' : 's'}`;
}
